import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Drawer, Box, IconButton, FormControl, InputLabel, Select, MenuItem, Divider, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { SCUIDateRanger, SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';

import OrderPrioCommand from '../../../services/command/orderPrio/OrderPrioCommand.services';
import { OPD } from './orderPrioDrawer.classes';

export const OrderPrioDrawer = ({
    isDrawerOpen,
    handleDrawerClose,
    editData,
    getGridData,
    prioReasons,
    prioTypes,
}: any) => {
    const [selectedPrioReason, setSelectedPrioReason] = useState('');
    const [selectedPrioType, setSelectedPrioType] = useState('');
    const [startDate, setStartDate] = useState<any>();
    const [endDate, setEndDate] = useState<any>();
    const [isActive, setIsActive] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isEdit = !!editData?.orderPriorityListId;

    const { t } = useTranslation();

    const handleDateChange = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const handleClearAndClose = () => {
        setIsActive(false);
        setEndDate(null);
        setStartDate(null);
        setSelectedPrioReason('');
        setSelectedPrioType('');
        handleDrawerClose();
    };

    const handleAddOrUpdate = () => {
        Swal.fire({
            text: t(!isEdit ? 'orderPrio.sureToAdd' : 'general.updateConfirmationText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t(!isEdit ? 'general.add' : 'general.update'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                const params: any = {
                    priorityReasonId: selectedPrioReason,
                    startDate: formatDateToT(startDate),
                    endDate: formatDateToT(endDate),
                    //status: isEdit ? editData?.status : 0,
                    priorityTypeId: selectedPrioType,
                    isActive,
                };
                if (isEdit) {
                    params.orderPriorityListId = editData?.orderPriorityListId;
                }

                OrderPrioCommand.AddOrUpdateOrderPrio(params)
                    .then((res: any) => {
                        if (res) {
                            setIsLoading(false);
                            Swal.fire(t('general.success'), '', 'success');
                            getGridData();
                            handleClearAndClose();
                        }
                    })
                    .catch(() => {
                        Swal.fire(t('general.anErrorOccured'), '', 'error');
                        setIsLoading(false);
                    });
            }
        });
    };

    const formatDate = (inputDate: string): string => {
        const date = new Date(inputDate);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        if (!day || !month || !year) {
            return '';
        }

        const formattedDay = String(day).padStart(2, '0');
        const formattedMonth = String(month).padStart(2, '0');
        return `${formattedDay}/${formattedMonth}/${year}`;
    };

    const formatDateToT = (inputDate: string): string => {
        const parts = inputDate.split('/');
        if (parts.length !== 3) {
            return '';
        }

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return '';
        }

        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`;
    };

    useEffect(() => {
        if (isEdit) {
            setSelectedPrioReason(editData.priorityReasonId);
            setSelectedPrioType(editData.priorityTypeId);
            setStartDate(formatDate(editData.startDate));
            setEndDate(formatDate(editData.endDate));
            setIsActive(editData.isActive);
        }
    }, [editData]);

    return (
        <Drawer
            variant="temporary"
            anchor="right"
            className={OPD.drawer}
            open={isDrawerOpen}
            onClose={() => handleClearAndClose()}
            sx={{
                '& .MuiDrawer-paper': {
                    padding: '16px',
                    width: 360,
                },
            }}
        >
            {isLoading ? (
                <div className={OPD.loadingWrap}>
                    <SCUILoadingLinear />
                </div>
            ) : (
                <></>
            )}
            <Box className={OPD.form} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    className={OPD.headerRow}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <p className={OPD.title} style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                        {isEdit ? t('orderPrio.editDrawerTitle') : t('orderPrio.addDrawerTitle')}
                    </p>
                    <IconButton
                        className={OPD.closeButton}
                        sx={{
                            marginLeft: 'auto',
                            '& .MuiSvgIcon-root': {
                                width: '2rem',
                                height: '2rem',
                                color: '#000',
                            },
                        }}
                        onClick={() => handleClearAndClose()}
                    >
                        <CloseIcon className={OPD.closeIcon} />
                    </IconButton>
                </Box>
                <Divider className={OPD.divider} sx={{ width: '100%', margin: '4px 0 24px 0' }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    <FormControl className={OPD.formControlReason}>
                        <InputLabel id="selectPrioReason">{t('orderPrio.selectPrioReason')}</InputLabel>
                        <Select
                            className={OPD.selectReason}
                            label={t('orderPrio.selectPrioReason')}
                            labelId="selectPrioReason"
                            id="selectPrioReason"
                            value={selectedPrioReason}
                            onChange={(event) => {
                                setSelectedPrioReason(event.target.value);
                            }}
                            fullWidth
                        >
                            {prioReasons &&
                                prioReasons.map((reason: any) => (
                                    <MenuItem key={reason.id} id={reason.id} value={reason.id}>
                                        {reason.description}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl className={OPD.formControlType}>
                        <InputLabel id="selectPrioType">{t('orderPrio.selectPrioType')}</InputLabel>
                        <Select
                            className={OPD.selectType}
                            label={t('orderPrio.selectPrioType')}
                            labelId="selectPrioType"
                            id="selectPrioType"
                            value={selectedPrioType}
                            onChange={(event) => {
                                setSelectedPrioType(event.target.value);
                            }}
                            fullWidth
                        >
                            {prioTypes &&
                                prioTypes.map((type: any) => (
                                    <MenuItem key={type.id} id={type.id} value={type.id}>
                                        {type.description}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <Box className={OPD.dateRangerWrap}>
                        <SCUIDateRanger
                            fromDate={startDate}
                            toDate={endDate}
                            setDateRange={handleDateChange}
                            dateFormat="DD/MM/YYYY"
                            label={t('general.startDate') + ' - ' + t('general.endDate')}
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <p
                            style={{
                                marginRight: '24px',
                                fontWeight: 'bold',
                                height: '36px',
                                fontSize: '1.2rem',
                                alignContent: 'center',
                            }}
                        >
                            {t('orderPrio.isEnabled')}:
                        </p>
                        <Switch
                            className={OPD.switchActive}
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    border: '0.5px solid #e0e0e0',
                                },
                            }}
                            checked={isActive}
                            onChange={(e: any) => setIsActive(e.target.checked)}
                        />
                    </Box>
                    <SCUIButton
                        className={OPD.submitButton}
                        text={isEdit ? t('general.edit') : t('general.add')}
                        type="primary"
                        sizeType="fixed"
                        size={'m'}
                        disabled={!selectedPrioReason || !selectedPrioType || !endDate || !startDate}
                        fullwidth
                        onClick={handleAddOrUpdate}
                    />
                </Box>
            </Box>
        </Drawer>
    );
};
